/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Created by Pak Kwan 2026-07-29: useAnimatedViewport for Phase 2
 * Provides smooth animated viewport transitions using framer-motion
 */

import { useCallback } from 'react';
import { animate, EasingDefinition } from 'framer-motion';
import { useUIStore } from '../stores/ui';

export type AnimatedViewportOptions = {
  duration?: number; // milliseconds (default: 150)
  easing?: EasingDefinition; // easing function (default: 'easeInOut')
};

/**
 * Hook to provide animated viewport transitions.
 * Returns a function that smoothly animates the viewport to a target position and scale.
 *
 * Default duration: 150ms - allows animation to complete before next throttled update
 * arrives (~67-143ms intervals from backend/frontend throttles). Prevents overlapping
 * animations when following during presentation mode.
 */
export function useAnimatedViewport() {
  const setBoardPosition = useUIStore((s) => s.setBoardPosition);
  const setScale = useUIStore((s) => s.setScale);

  const animateTo = useCallback(
    (
      targetX: number,
      targetY: number,
      targetScale: number,
      options: AnimatedViewportOptions = {}
    ) => {
      const { duration = 150, easing = 'easeInOut' } = options;

      // Get current viewport state
      const currentPos = useUIStore.getState().boardPosition;
      const currentScale = useUIStore.getState().scale;

      // Animation controls (to allow cancellation if needed)
      const controls: any[] = [];

      // Animate X position
      const xControl = animate(currentPos.x, targetX, {
        duration: duration / 1000, // framer-motion uses seconds
        ease: easing,
        onUpdate: (x: number) => {
          const pos = useUIStore.getState().boardPosition;
          setBoardPosition({ x, y: pos.y });
        },
      });
      controls.push(xControl);

      // Animate Y position
      const yControl = animate(currentPos.y, targetY, {
        duration: duration / 1000,
        ease: easing,
        onUpdate: (y: number) => {
          const pos = useUIStore.getState().boardPosition;
          setBoardPosition({ x: pos.x, y });
        },
      });
      controls.push(yControl);

      // Animate scale
      const scaleControl = animate(currentScale, targetScale, {
        duration: duration / 1000,
        ease: easing,
        onUpdate: (scale: number) => {
          setScale(scale);
        },
      });
      controls.push(scaleControl);

      // Return a cancel function
      return () => {
        controls.forEach((control) => control.stop());
      };
    },
    [setBoardPosition, setScale]
  );

  return { animateTo };
}
